/**
 * Delta T (wet-bulb depression) helpers, shared across the spray module.
 *
 * Delta T = dry-bulb temperature − wet-bulb temperature
 *
 * Used to gate spray decisions: very low Delta T (cool + humid) means
 * droplets don't evaporate and risk run-off / drift; very high Delta T
 * (hot + dry) means droplets evaporate before they reach the canopy.
 * Industry guidance is roughly 2–8 °C preferred, 8–10 °C marginal,
 * >10 °C unsuitable.
 *
 * computeDeltaT uses Stull (2011) — a closed-form psychrometric
 * approximation that takes both dry-bulb temperature and relative
 * humidity, matching the agronomic Delta T chart contours to ~0.3 °C
 * across the chart's normal range. Replaces the earlier
 *   (100 − RH) / 5
 * one-variable rule of thumb, which only matched Stull around 25 °C
 * dry-bulb and drifted up to ~6 °C off at temperature extremes.
 *
 * Reference: Stull, R. (2011). Wet-Bulb Temperature from Relative
 * Humidity and Air Temperature. Journal of Applied Meteorology and
 * Climatology, 50(11), 2267–2269.
 */

/**
 * Wet-bulb temperature (°C) from dry-bulb temperature (°C) and
 * relative humidity (%). Stull (2011) closed-form approximation.
 *
 * Valid across roughly −20 °C to +50 °C dry-bulb and 5–99 % RH; outside
 * that envelope the formula degrades gracefully but operators won't be
 * spraying anyway.
 *
 * @param {number} T  dry-bulb temperature in °C
 * @param {number} RH relative humidity in % (0-100)
 * @returns {number}  wet-bulb temperature in °C
 */
export function computeWetBulb(T, RH) {
  // Clamp RH to the formula's safe range. The arctangent terms diverge
  // near RH=0 and produce nonsense at RH<5; clamp to avoid silently
  // returning values that look plausible but aren't.
  const safeRH = Math.min(100, Math.max(5, RH));
  return (
    T * Math.atan(0.151977 * Math.sqrt(safeRH + 8.313659))
    + Math.atan(T + safeRH)
    - Math.atan(safeRH - 1.676331)
    + 0.00391838 * Math.pow(safeRH, 1.5) * Math.atan(0.023101 * safeRH)
    - 4.686035
  );
}

/**
 * Delta T (°C) from dry-bulb temperature (°C) and relative humidity (%).
 * Convenience wrapper around computeWetBulb.
 *
 * Returns null if either input is missing — callers can fall back to a
 * dash in the UI rather than rendering a meaningless number.
 *
 * @param {number|null|undefined} T   dry-bulb temperature in °C
 * @param {number|null|undefined} RH  relative humidity in %
 * @returns {number|null}  Delta T in °C, or null when inputs are missing
 */
export function computeDeltaT(T, RH) {
  if (T == null || RH == null || Number.isNaN(T) || Number.isNaN(RH)) {
    return null;
  }
  const dT = T - computeWetBulb(T, RH);
  // Delta T should never be negative in normal conditions; clamp so a
  // floating-point edge case at very high humidity doesn't render as
  // "-0.01 °C" on the operator's screen.
  return Math.max(0, dT);
}
